import React, { Component } from "react";
import { NavLink, Navigate } from "react-router-dom";
import { Props, State } from "../models/navbar.models";
import withRouter from "./withRouter";
import { connect } from "react-redux";
import { setActiveHotel } from "../redux/Slice/hotelSlice";
import { setError } from "../redux/Slice/state";
import { YieldApi } from "api/yield";
import { ConfigApi } from "api/config";
import { UserApi } from "api/user";
import { baseTheme } from "../mui-theme";
import PasswordValidator from "password-validator";

import {
  TextField,
  Autocomplete,
  ThemeProvider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Popup from "../reusable-components/popup/popup";
import {
  GridOn,
  AccountCircle,
  Apartment,
  Settings,
  VpnKey,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
  Logout,
  ArrowForward,
} from "@mui/icons-material";
import { resetYieldsheet } from "redux/Slice/yieldssheetSlice";
import { store } from "redux/store";

let logo: string;
import(`assets/images/${process.env.REACT_APP_STAGE}/navbar-logo.png`).then(
  (module) => {
    logo = module.default;
  },
);

class Navbar extends Component<Props, State> {
  readonly state = {
    dropdownActive: false,
    onboardHotel: false,
    dropDownOpen: false,
    dropdownValue: "",
    inputValue: "",
    counter: 0,
    showResetPasswordDialog: false,
    showPasswordSuccessDialog: false,
    currentPassword: "",
    newPassword: "",
    repeatNewPassword: "",
    showCurrentPassword: false,
    showNewPassword: false,
    showRepeatPassword: false,
    resetPasswordError: "",
    resetPasswordLoading: false,
    currentPasswordError: false,
    redirectingNewExperience: false,
  };

  // Password validation schema
  private passwordSchema = new PasswordValidator()
    .is()
    .min(8) // Minimum length 8
    .is()
    .max(20) // Maximum length 20
    .has()
    .uppercase() // Must have uppercase letters
    .has()
    .lowercase() // Must have lowercase letters
    .has()
    .digits() // Must have digits
    .has()
    .symbols() // Must have symbols
    .has()
    .not()
    .spaces(); // Should not have spaces

  // Get password validation results
  private getPasswordValidation = (password: string) => {
    const failedRules = this.passwordSchema.validate(password, {
      list: true,
    }) as string[];
    return {
      minLength: !failedRules.includes("min"),
      maxLength: !failedRules.includes("max"),
      hasUppercase: !failedRules.includes("uppercase"),
      hasLowercase: !failedRules.includes("lowercase"),
      hasDigits: !failedRules.includes("digits"),
      hasSymbols: !failedRules.includes("symbols"),
      noSpaces: !failedRules.includes("spaces"),
      isValid: failedRules.length === 0,
    };
  };

  // Render password requirements checklist
  private renderPasswordRequirements = () => {
    try {
      // Always show requirements, even if password is empty
      const password = this.state.newPassword || "";
      const validation = this.getPasswordValidation(password);
      const requirements = [
        {
          key: "length",
          text: "Contains 8-20 characters",
          valid: validation.minLength && validation.maxLength,
        },
        {
          key: "hasUppercase",
          text: "Contains uppercase letter",
          valid: validation.hasUppercase,
        },
        {
          key: "hasLowercase",
          text: "Contains lowercase letter",
          valid: validation.hasLowercase,
        },
        {
          key: "hasDigits",
          text: "Contains number",
          valid: validation.hasDigits,
        },
        {
          key: "hasSymbols",
          text: "Contains symbol",
          valid: validation.hasSymbols,
        },
        { key: "noSpaces", text: "No spaces", valid: validation.noSpaces },
      ];

      return requirements.map((req) => (
        <div
          key={req.key}
          style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}
        >
          {req.valid ? (
            <CheckCircle
              style={{ color: "#4caf50", fontSize: "16px", marginRight: "8px" }}
            />
          ) : (
            <Cancel
              style={{ color: "#f44336", fontSize: "16px", marginRight: "8px" }}
            />
          )}
          <span
            style={{
              fontSize: "12px",
              color: req.valid ? "#4caf50" : "#f44336",
            }}
          >
            {req.text}
          </span>
        </div>
      ));
    } catch (error) {
      console.error("Error rendering password requirements:", error);
      return (
        <div style={{ color: "#f44336", fontSize: "12px" }}>
          Error loading password requirements
        </div>
      );
    }
  };

  componentDidMount() {
    window.addEventListener("click", this.closeDropdown);
    this.setState({ counter: this.state.counter + 1 });
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.closeDropdown);
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevState.dropdownValue !== this.state.dropdownValue) {
      this.setState({ dropDownOpen: false });
    }
    // If route changed, ensure the new experience button state resets
    if (
      prevProps.router?.location?.pathname !==
        this.props.router?.location?.pathname &&
      this.state.redirectingNewExperience
    ) {
      this.setState({ redirectingNewExperience: false });
    }
  }

  onboardHotel = () => {
    this.setState({ onboardHotel: true });
  };

  handleOnLogout = () => {
    this.props.onLogout();
    setTimeout(() => {
      this.props.router.navigate("/");
    }, 500);
  };

  handleGoToNewExperienceClick = async () => {
    try {
      this.setState({ redirectingNewExperience: true });
      const { onGoToNewExperience } = this.props;
      await Promise.resolve(onGoToNewExperience?.());
    } catch (error) {
      console.error("Error redirecting to new experience", error);
      throw new Error("Failed to redirect to new experience");
    } finally {
      this.handleOnLogout();
    }
  };

  handleOpenResetPasswordDialog = () => {
    this.setState({
      showResetPasswordDialog: true,
      currentPassword: "",
      newPassword: "",
      repeatNewPassword: "",
      resetPasswordError: "",
      currentPasswordError: false,
      dropdownActive: false, // Close the dropdown
    });
  };

  handleCloseResetPasswordDialog = () => {
    this.setState({
      showResetPasswordDialog: false,
      currentPassword: "",
      newPassword: "",
      repeatNewPassword: "",
      resetPasswordError: "",
      showCurrentPassword: false,
      showNewPassword: false,
      showRepeatPassword: false,
      resetPasswordLoading: false,
      currentPasswordError: false,
    });
  };

  handleResetPasswordSubmit = async () => {
    const { currentPassword, newPassword, repeatNewPassword } = this.state;

    // Validation
    if (!currentPassword || !newPassword || !repeatNewPassword) {
      this.setState({
        resetPasswordError: "All fields are required",
        currentPasswordError: false,
      });
      return;
    }

    if (newPassword !== repeatNewPassword) {
      this.setState({
        resetPasswordError: "New passwords do not match",
        currentPasswordError: false,
      });
      return;
    }

    // Validate password using comprehensive schema
    const passwordValidation = this.getPasswordValidation(newPassword);
    if (!passwordValidation.isValid) {
      this.setState({
        resetPasswordError:
          "Password does not meet all requirements. Please check the requirements below.",
        currentPasswordError: false,
      });
      return;
    }

    this.setState({
      resetPasswordLoading: true,
      resetPasswordError: "",
      currentPasswordError: false,
    });

    try {
      await UserApi.selfReset({
        email: this.props.username,
        currentPassword,
        newPassword,
      });

      // Success - close reset dialog and show success dialog
      this.handleCloseResetPasswordDialog();
      this.setState({ showPasswordSuccessDialog: true });
    } catch (error: any) {
      // Check if the error is specifically about incorrect current password
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to reset password. Please try again.";
      const isIncorrectPassword = errorMessage.includes(
        "Current password is incorrect",
      );

      this.setState({
        resetPasswordError: isIncorrectPassword
          ? "Incorrect current password"
          : errorMessage,
        resetPasswordLoading: false,
        currentPasswordError: isIncorrectPassword,
      });
    }
  };

  openHotelDropDown = () => {
    this.setState({ dropDownOpen: true });
  };

  async changeHotel(event: any) {
    if (event) {
      const target = event["id"];
      const loc = this.props.router.location.pathname;
      const parts = loc.split("/");
      const exceptId = parts
        .splice(2, parts.length - 1)
        .join("/")
        .toString();

      this.props.router.navigate(`${target}/${exceptId}`);

      if (this.props.hotels && this.props.hotels.length !== 0) {
        if (target && !isNaN(parseInt(target))) {
          try {
            const h = await ConfigApi.getHotelConfig(parseInt(target));
            this.props.setActiveHotel(h);
          } catch (err) {
            console.error(err);
            this.props.setError(true);
            throw err;
          }
        }
      }

      store.dispatch(resetYieldsheet());

      this.setState({
        dropdownValue: event["label"],
        dropDownOpen: false,
      });
    }
  }

  render() {
    const dropdownClass = this.state.dropdownActive ? "show" : "";
    const {
      activeHotel,
      hotels,
      showNewExperienceButton,
      isNewExperienceLoading,
      onGoToNewExperience,
    } = this.props;

    const hotelName =
      hotels?.map((data: any) => {
        return { label: data.name, id: data.hotelId };
      }) || [];

    const currentURL = this.props.router.location.pathname;
    const part = currentURL.split("/");
    const allRoutes = ["yield-sheet", "settings", "executive-summary"];
    const settingsRoute = [
      "hotel-config",
      "comp-set-config",
      "room-config",
      "users",
    ];

    if (part[2] && !allRoutes.includes(part[2])) {
      return <Navigate to={`/${activeHotel?.hotelId}`} />;
    }

    if (part[2] === "settings") {
      if (part[2] !== "settings" && !settingsRoute.includes(part[3])) {
        return <Navigate to={`/${activeHotel?.hotelId}`} />;
      }
    }

    return (
      <ThemeProvider theme={baseTheme}>
        <nav
          className="navbar navbar-expand navbar-dark bg-dark mb-4"
          id="navbar"
          style={{ position: "fixed", width: "100%", zIndex: "1000" }}
        >
          <NavLink to="" end>
            <img
              className="logo"
              src={logo}
              alt="Revanista Logo"
              id="navbar-revanista-logo"
            />
          </NavLink>
          {currentURL !== "/" &&
            currentURL !== "/onboard-hotel" &&
            currentURL !== "/admin" &&
            activeHotel?.info?.fullName && (
              <span className="hotelName">
                {" " + activeHotel.info.fullName}
              </span>
            )}
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="revNavBar">
              {currentURL !== "/" &&
                currentURL !== "/admin" &&
                currentURL !== "/onboard-hotel" &&
                activeHotel?.info?.fullName && (
                  <>
                    {hotelName.length > 1 && (
                      <li className="hotelDropdown">
                        <span>
                          <Apartment
                            color="primary"
                            sx={{
                              cursor: "pointer",
                              marginRight: "10px",
                            }}
                            onClick={this.openHotelDropDown}
                            aria-label="Select Hotel"
                          />
                        </span>
                        <Popup
                          isPopupOpen={this.state.dropDownOpen}
                          title="Select Hotel"
                          content={[
                            <div
                              key="autocomplete-container"
                              style={{ width: "100%", padding: "8px" }}
                            >
                              <Autocomplete
                                value={activeHotel?.info?.name || ""}
                                onChange={(event: any, newValue: any) => {
                                  if (newValue) {
                                    this.changeHotel(newValue);
                                  }
                                }}
                                options={hotelName}
                                fullWidth
                                getOptionLabel={(option: any) =>
                                  typeof option === "string"
                                    ? option
                                    : option.label
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Search Hotel"
                                    variant="outlined"
                                    fullWidth
                                    InputProps={{
                                      ...params.InputProps,
                                      style: {
                                        fontSize: "1.1rem",
                                        padding: "8px 4px",
                                      },
                                    }}
                                  />
                                )}
                                ListboxProps={{
                                  style: {
                                    maxHeight: "300px",
                                    fontSize: "1.1rem",
                                  },
                                }}
                              />
                            </div>,
                          ]}
                          action={() => this.setState({ dropDownOpen: false })}
                          actionName="Close"
                          maxWidth="sm"
                          cancelAction={() =>
                            this.setState({ dropDownOpen: false })
                          }
                          closeOnOutsideClick={true}
                        />
                      </li>
                    )}
                    {currentURL === `/${part[1]}` ||
                    (currentURL === `/${part[1]}/` &&
                      !currentURL.includes(`/${part[1]}/settings`)) ? (
                      <li className="nav-item ml-auto">
                        <NavLink
                          to={`${part[1]}/settings/executive-summary`}
                          end
                          className="nav-link"
                        >
                          <span style={{ color: "white" }}>
                            <Settings color="primary" />
                            &nbsp;Settings
                          </span>
                        </NavLink>
                      </li>
                    ) : (
                      <li className="nav-item ml-auto">
                        <NavLink to={`${part[1]}`} end className="nav-link">
                          <span style={{ color: "white" }}>
                            <GridOn color="primary" />
                            &nbsp;Yield Sheet
                          </span>
                        </NavLink>
                      </li>
                    )}

                    {showNewExperienceButton && (
                      <li className="nav-item ml-auto">
                        <button
                          className="nav-link new-experience-button"
                          onClick={this.handleGoToNewExperienceClick}
                          disabled={this.state.redirectingNewExperience}
                        >
                          <span
                            style={{
                              color: "white",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            &nbsp;
                            {this.state.redirectingNewExperience
                              ? "Redirecting..."
                              : "Go to Revanista 2.0"}
                          </span>
                        </button>
                      </li>
                    )}
                  </>
                )}
              <li className={`nav-item dropdown-bar${dropdownClass}`}>
                <a
                  className="nav-link dropdown-toggle"
                  id="userDropdown"
                  role="button"
                  onClick={this.toggleDropdown}
                  style={{ color: "white" }}
                >
                  <AccountCircle color="primary" />
                  &nbsp;{this.props.username}
                </a>
                <div
                  className={`dropdown-menu dropdown-menu-right ${dropdownClass}`}
                >
                  <a
                    className="dropdown-item"
                    role="button"
                    onClick={this.handleOpenResetPasswordDialog}
                  >
                    <VpnKey style={{ marginRight: "8px", fontSize: "18px" }} />
                    Reset Password
                  </a>
                  <a
                    className="dropdown-item"
                    role="button"
                    onClick={this.handleOnLogout}
                  >
                    <Logout style={{ marginRight: "8px", fontSize: "18px" }} />
                    Log out
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </nav>

        {/* Reset Password Dialog */}
        <Dialog
          open={this.state.showResetPasswordDialog}
          onClose={this.handleCloseResetPasswordDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Reset Password</DialogTitle>
          <DialogContent>
            {this.state.resetPasswordError && (
              <div
                style={{ color: "red", marginBottom: "16px", fontSize: "14px" }}
              >
                {this.state.resetPasswordError}
              </div>
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Current Password"
              type={this.state.showCurrentPassword ? "text" : "password"}
              value={this.state.currentPassword}
              onChange={(e) =>
                this.setState({
                  currentPassword: e.target.value,
                  currentPasswordError: false,
                  resetPasswordError: "",
                })
              }
              error={this.state.currentPasswordError}
              helperText={
                this.state.currentPasswordError
                  ? "Incorrect current password"
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        this.setState({
                          showCurrentPassword: !this.state.showCurrentPassword,
                        })
                      }
                      edge="end"
                    >
                      {this.state.showCurrentPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="New Password"
              type={this.state.showNewPassword ? "text" : "password"}
              value={this.state.newPassword}
              onChange={(e) => this.setState({ newPassword: e.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        this.setState({
                          showNewPassword: !this.state.showNewPassword,
                        })
                      }
                      edge="end"
                    >
                      {this.state.showNewPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Requirements Checklist */}
            <div
              style={{
                marginTop: "16px",
                marginBottom: "8px",
                border: "1px solid #e0e0e0",
                padding: "12px",
                borderRadius: "4px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#666",
                }}
              >
                Password Requirements:
              </div>
              {this.renderPasswordRequirements()}
            </div>

            <TextField
              fullWidth
              margin="normal"
              label="Repeat New Password"
              type={this.state.showRepeatPassword ? "text" : "password"}
              value={this.state.repeatNewPassword}
              onChange={(e) =>
                this.setState({ repeatNewPassword: e.target.value })
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        this.setState({
                          showRepeatPassword: !this.state.showRepeatPassword,
                        })
                      }
                      edge="end"
                    >
                      {this.state.showRepeatPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleCloseResetPasswordDialog}
              disabled={this.state.resetPasswordLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleResetPasswordSubmit}
              variant="contained"
              disabled={
                this.state.resetPasswordLoading ||
                !this.getPasswordValidation(this.state.newPassword || "")
                  .isValid ||
                !this.state.currentPassword ||
                !this.state.newPassword ||
                !this.state.repeatNewPassword ||
                this.state.newPassword !== this.state.repeatNewPassword
              }
            >
              {this.state.resetPasswordLoading
                ? "Changing..."
                : "Change Password"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Password Success Dialog */}
        <Dialog
          open={this.state.showPasswordSuccessDialog}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
          onClose={undefined} // Prevent closing
        >
          <DialogTitle>Password Update</DialogTitle>
          <DialogContent>
            <p>
              Your password has been changed successfully. For security reasons,
              you have been signed out from all devices.
            </p>
            <p>
              You will be redirected to the login page when you click
              &ldquo;Continue&rdquo;.
            </p>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleOnLogout}
              variant="contained"
              color="primary"
              size="large"
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  }

  private closeDropdown = (event: MouseEvent) => {
    if (!(event.target as HTMLElement).classList.contains("dropdown-toggle")) {
      this.setState({ dropdownActive: false });
    }
  };

  private toggleDropdown = () => {
    this.setState({ dropdownActive: !this.state.dropdownActive });
  };
}

const mapStateToProps = (state: any) => ({
  activeHotel: state.hotelData.activeHotel,
  hotels: state.hotelData.hotels,
});

const mapDispatchToProps = (dispatch: any) => ({
  setActiveHotel: (hotel: any) => dispatch(setActiveHotel(hotel)),
  setError: (error: boolean) => dispatch(setError(error)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Navbar));
